import { Box, Button, Checkbox, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import EditIcon from '@mui/icons-material/Edit';
import Header from '../../../components/Header';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { can, filterActionColumns } from '../../../utils/permissions';

const AllGuides = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);

  const [allGuides, setAllGuides] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'desc' }]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const adminUserJson = localStorage.getItem('adminUser');
  const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
  const isCoordinator = Number(adminUser?.role) === 3;

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
      setSelectedRows([]);
      setSelectAllMode(false);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async () => {
    setLoading(true);

    const sortBy = sortModel.length ? sortModel[0].field : 'id';
    const sortOrder = sortModel.length && sortModel[0].sort === 'desc' ? 'DESC' : 'ASC';

    const res = await fetch(
      `${
        process.env.REACT_APP_APILINK
      }/guides?page=${page}&limit=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}&search=${encodeURIComponent(
        search
      )}`,
      { headers: { token } }
    );

    const data = await res.json();
    if (data.success) {
      setAllGuides(data.guides);
      setTotalCount(data.pagination.total);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, [page, pageSize, sortModel, search]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Delete single guide
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/guides/${id}`, { method: 'DELETE', headers: { token } });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) getData();
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  // Select all across pages
  const handleSelectAllAcrossPages = async () => {
    setSelectAllLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/guides/ids?search=${encodeURIComponent(search)}`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success) {
        setSelectedRows(data.ids);
        setSelectAllMode(true);
      }
    } catch (e) {
      console.error('Error fetching all guide IDs', e);
    }
    setSelectAllLoading(false);
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setSelectAllMode(false);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/guides/bulk`, {
      method: 'DELETE',
      headers: { token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selectedRows }),
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        setSelectedRows([]);
        setSelectAllMode(false);
        getData();
      }
    }
    setBulkDeleteDialogOpen(false);
  };

  const allPageSelected = allGuides.length > 0 && allGuides.every((r) => selectedRows.includes(r.id));
  const somePageSelected = selectedRows.length > 0 && !allPageSelected;

  const selectColumn = {
    field: '__select__',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderHeader: () => (
      <Checkbox
        checked={selectAllMode || allPageSelected}
        indeterminate={!selectAllMode && somePageSelected}
        disabled={selectAllLoading}
        sx={{ color: `${colors.greenAccent[200]} !important` }}
        onChange={(e) => {
          if (e.target.checked) {
            handleSelectAllAcrossPages();
          } else {
            handleClearSelection();
          }
        }}
      />
    ),
    renderCell: (params) => (
      <Checkbox
        checked={selectedRows.includes(params.row.id)}
        sx={{ color: `${colors.greenAccent[200]} !important` }}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRows((prev) => [...prev, params.row.id]);
          } else {
            setSelectedRows((prev) => prev.filter((id) => id !== params.row.id));
            if (selectAllMode) setSelectAllMode(false);
          }
        }}
      />
    ),
  };

  const dataColumns = [
    {
      field: 'fullName',
      headerName: 'Guide Full Name',
      flex: 1,
      minWidth: 100,
      sortable: false,
      cellClassName: 'name-column--cell',
      valueGetter: ({ row: { name, surname } }) => (name || surname ? `${name} ${surname}` : '-'),
    },
    {
      field: 'email',
      headerName: 'Guide Email',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'address',
      headerName: 'Guide Address',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'phone',
      headerName: 'Guide Phone No',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'license',
      headerName: 'Guide License',
      flex: 1,
      minWidth: 100,
      filterable: false,
      sortable: false,
    },
    ...(isCoordinator
      ? [
          {
            field: 'View',
            filterable: false,
            sortable: false,
            headerName: 'Action',
            renderCell: (cellValues) => (
              <Link to={`/trip-reports?guideId=${cellValues.row.id}`} style={{ textDecoration: 'none' }}>
                <Button
                  size="small"
                  sx={{
                    backgroundColor: colors.blueAccent[600],
                    color: colors.grey[100],
                    ':hover': { backgroundColor: colors.blueAccent[700] },
                  }}
                  variant="contained"
                >
                  <VisibilityIcon />
                </Button>
              </Link>
            ),
          },
        ]
      : [
          {
            field: 'Duplicate',
            filterable: false,
            sortable: false,
            renderCell: (cellValues) => (
              <Link to={`/guide/addguide?duplicateId=${cellValues.row.id}`}>
                <Button
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    ':hover': { backgroundColor: colors.blueAccent[800] },
                  }}
                  variant="contained"
                >
                  <ContentCopyIcon />
                </Button>
              </Link>
            ),
          },
          {
            field: 'Edit',
            filterable: false,
            sortable: false,
            renderCell: (cellValues) => (
              <Link to={`/guide/${cellValues.row.id}`}>
                <Button
                  sx={{
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100],
                    ':hover': { backgroundColor: colors.greenAccent[700] },
                  }}
                  variant="contained"
                >
                  <EditIcon />
                </Button>
              </Link>
            ),
          },
          {
            field: 'Delete',
            filterable: false,
            sortable: false,
            renderCell: (cellValues) => (
              <Button
                sx={{
                  backgroundColor: colors.redAccent[500],
                  color: colors.grey[100],
                  ':hover': { backgroundColor: colors.redAccent[600] },
                }}
                variant="contained"
                onClick={() => openDeleteDialog(cellValues.row.id)}
              >
                <DeleteIcon />
              </Button>
            ),
          },
        ]),
  ];

  const columns = isCoordinator ? dataColumns : [selectColumn, ...dataColumns];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="GUIDES" subtitle={isCoordinator ? 'Guides (view only)' : 'All guides'} />
        {!isCoordinator && (
          <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center">
            {selectedRows.length > 0 && can('guides.delete') && (
              <Button
                sx={{
                  backgroundColor: colors.redAccent[500],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': { backgroundColor: colors.redAccent[600] },
                }}
                variant="contained"
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                <DeleteIcon sx={{ mr: '10px' }} />
                Delete Selected ({selectedRows.length})
              </Button>
            )}
            {can('guides.create') && (
              <Link to="/guide/import">
                <Button
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '10px 20px',
                    '&:hover': { backgroundColor: colors.blueAccent[800] },
                  }}
                >
                  <DownloadIcon sx={{ mr: '10px' }} />
                  Import Guides
                </Button>
              </Link>
            )}
            {can('guides.create') && (
              <Link to="/guide/addguide">
                <Button
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '10px 20px',
                    '&:hover': { backgroundColor: colors.blueAccent[800] },
                  }}
                >
                  <PeopleOutlined sx={{ mr: '10px' }} />
                  Add Guide
                </Button>
              </Link>
            )}
          </Box>
        )}
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by name, address, email, phone..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
      </Box>

      {search && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          <Chip
            label={`Search: ${search}`}
            size="small"
            onDelete={handleClearSearch}
            sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
          />
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.greenAccent[300] },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={allGuides}
          columns={filterActionColumns(columns, {
            Edit: 'guides.edit',
            Duplicate: 'guides.create',
            Delete: 'guides.delete',
          })}
          getRowId={(row) => row.id}
          disableColumnFilter
          pageSize={pageSize}
          rowsPerPageOptions={[10]}
          rowCount={totalCount}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          sortingMode="server"
          onSortModelChange={(model) => setSortModel(model)}
          loading={loading}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete guide?"
        message="Are you sure you want to delete this guide?"
        colors={colors}
      />

      <ConfirmDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedRows.length} guide(s)?`}
        message={`Are you sure you want to delete ${selectedRows.length} selected guide(s)? This action cannot be undone.`}
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AllGuides;
